import { useMemo } from 'react'

import {
  F0FormSubmitResult,
  F0WizardFormPerSectionSubmitArg,
  F0WizardFormStep,
  useF0FormDefinition,
} from '@factorialco/f0-react'
import { z } from 'zod'

import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import i18n from 'modules/core/lib/i18n'

import {
  buildCommentsSchema,
  commentsDefaults,
  commentsSection,
} from 'modules/trainings/pages/MyTrainings/Requests/shared/comments'
import {
  buildEmployeesSchema,
  employeesSection,
} from 'modules/trainings/pages/MyTrainings/Requests/shared/employees'
import {
  buildTrainingNeedSchema,
  trainingNeedDefaults,
  trainingNeedSection,
} from 'modules/trainings/pages/MyTrainings/Requests/shared/trainingNeed'

import {
  buildTrainingSelectorSchema,
  trainingSelectorDefaults,
  trainingSelectorSection,
} from './sections/trainingSelector'

const EMPTY_STRING_ARRAY: string[] = []

export interface UseFromCatalogFormParams {
  showParticipantsStep: boolean
  showTrainingSelector: boolean
  defaultEmployeeId?: string
  defaultTrainingId?: string
  onSubmit: (arg: F0WizardFormPerSectionSubmitArg<FromCatalogSchema>) => Promise<F0FormSubmitResult>
}

// -- Exported types --

function buildFromCatalogSchema(params: {
  canUseRichTextEditor: boolean
  richTextEditorLoading: boolean
  editorConfig: ReturnType<typeof useRichTextEditor>['editorConfig']
}) {
  return {
    trainingNeed: buildTrainingNeedSchema({
      canUseRichTextEditor: params.canUseRichTextEditor,
      richTextEditorLoading: params.richTextEditorLoading,
      editorConfig: params.editorConfig,
    }),
    trainingSelector: buildTrainingSelectorSchema(),
    employees: buildEmployeesSchema(),
    comments: buildCommentsSchema({
      canUseRichTextEditor: params.canUseRichTextEditor,
      richTextEditorLoading: params.richTextEditorLoading,
      editorConfig: params.editorConfig,
    }),
  }
}

export type FromCatalogSchema = ReturnType<typeof buildFromCatalogSchema>

export type FromCatalogFormValues = {
  [K in keyof FromCatalogSchema]: z.infer<FromCatalogSchema[K]>
}

// -- Hook --

export const useFromCatalogForm = ({
  showParticipantsStep,
  showTrainingSelector,
  defaultEmployeeId,
  defaultTrainingId,
  onSubmit,
}: UseFromCatalogFormParams) => {
  const { editorConfig, canUseRichTextEditor, loading: richTextEditorLoading } = useRichTextEditor()

  const schema = useMemo(
    () =>
      buildFromCatalogSchema({
        canUseRichTextEditor,
        richTextEditorLoading,
        editorConfig,
      }),
    [canUseRichTextEditor, richTextEditorLoading, editorConfig]
  )

  const defaultValues = useMemo(
    () => ({
      trainingNeed: trainingNeedDefaults,
      trainingSelector: trainingSelectorDefaults(defaultTrainingId),
      employees: {
        employeesSelection: defaultEmployeeId
          ? {
              type: 'manual' as const,
              ids: [defaultEmployeeId],
            }
          : {
              type: 'manual' as const,
              ids: EMPTY_STRING_ARRAY,
            },
      },
      comments: commentsDefaults,
    }),
    [defaultEmployeeId, defaultTrainingId]
  )

  const sections = useMemo(
    () => ({
      trainingNeed: trainingNeedSection(),
      trainingSelector: trainingSelectorSection(),
      employees: employeesSection(),
      comments: commentsSection(),
    }),
    []
  )

  const steps: F0WizardFormStep[] = useMemo(() => {
    const base: F0WizardFormStep[] = [
      {
        title: trainingNeedSection().title,
        sectionIds: ['trainingNeed'],
      },
    ]

    if (showTrainingSelector) {
      base.push({
        title: trainingSelectorSection().title,
        sectionIds: ['trainingSelector'],
      })
    }

    if (showParticipantsStep) {
      base.push({
        title: employeesSection().title,
        sectionIds: ['employees'],
      })
    }

    base.push({
      title: commentsSection().title,
      nextLabel: i18n.t('trainings.training_requests.create.send_for_approval'),
      sectionIds: ['comments'],
    })

    return base
  }, [showTrainingSelector, showParticipantsStep])

  const formDefinition = useF0FormDefinition({
    name: 'from-catalog-wizard',
    schema,
    sections,
    defaultValues,
    onSubmit,
  })

  return { formDefinition, steps }
}
